import { useState } from 'react';
import { motion } from 'motion/react';
import { Eye, EyeOff, X, Mail, Lock, User, ArrowRight, Check } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import { useNotificationsStore } from '@/stores/notifications-store';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

interface RegisterProps {
  onLogin: () => void;
  onClose: () => void;
}

export default function Register({ onLogin, onClose }: RegisterProps) {
  const { register, isLoading } = useAuthStore();
  const { showToast } = useUIStore();
  const { notifyWelcome } = useNotificationsStore();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!name || name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      await register(email, password, name);
      notifyWelcome(name);
      showToast('Account created successfully!', 'success');
      onClose();
    } catch (err) {
      showToast((err as Error).message || 'Registration failed', 'error');
    }
  };

  // Password strength indicators
  const passwordChecks = [
    { label: 'At least 6 characters', valid: password.length >= 6 },
    { label: 'Contains a number', valid: /\d/.test(password) },
    { label: 'Contains uppercase', valid: /[A-Z]/.test(password) },
  ];

  const clearError = (field: keyof typeof errors) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-background z-50 flex flex-col overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -left-1/4 w-[500px] h-[500px] rounded-full bg-accent-purple/10 blur-[100px]" />
        <div className="absolute -bottom-1/4 -right-1/4 w-[400px] h-[400px] rounded-full bg-accent-blue/10 blur-[80px]" />
      </div>

      {/* Safe Area Top */}
      <div className="safe-area-top" />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="relative flex items-center justify-between px-4 py-3"
      >
        <button
          onClick={onClose}
          className="w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <X size={20} />
        </button>
        <div />
      </motion.div>

      {/* Content */}
      <div className="relative flex-1 px-6 flex flex-col overflow-y-auto">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mt-2 mb-6"
        >
          <h1 className="text-3xl font-bold text-center mb-2">Create Account</h1>
          <p className="text-white/50 text-center">Join VisionAR and explore AR experiences</p>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="space-y-3"
        >
          {/* Name */}
          <Input
            type="text"
            label="Full Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearError('name');
            }}
            placeholder="Your name"
            icon={<User size={20} />}
            error={errors.name}
            autoComplete="name"
          />

          {/* Email */}
          <Input
            type="email"
            label="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearError('email');
            }}
            placeholder="your@email.com"
            icon={<Mail size={20} />}
            error={errors.email}
            autoComplete="email"
          />

          {/* Password */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-white/70 ml-1">Password</label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 z-10">
                <Lock size={20} />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearError('password');
                }}
                placeholder="Min. 6 characters"
                autoComplete="new-password"
                className={`
                  w-full glass rounded-2xl px-4 py-3.5 min-h-[52px] pl-12 pr-12
                  text-white placeholder:text-white/30
                  outline-none border border-transparent
                  transition-all duration-200
                  focus:border-accent-blue/50 focus:bg-white/[0.08]
                  hover:bg-white/[0.06]
                  ${errors.password ? 'border-red-500/50 bg-red-500/5' : ''}
                `}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors p-1"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-red-400 ml-1"
              >
                {errors.password}
              </motion.p>
            )}

            {/* Password strength */}
            {password && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="flex flex-wrap gap-2 mt-2 ml-1"
              >
                {passwordChecks.map((check) => (
                  <div
                    key={check.label}
                    className={`flex items-center gap-1 text-xs ${
                      check.valid ? 'text-green-400' : 'text-white/30'
                    }`}
                  >
                    <Check size={12} className={check.valid ? 'opacity-100' : 'opacity-0'} />
                    <span>{check.label}</span>
                  </div>
                ))}
              </motion.div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-white/70 ml-1">Confirm Password</label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 z-10">
                <Lock size={20} />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  clearError('confirmPassword');
                }}
                placeholder="Confirm your password"
                autoComplete="new-password"
                className={`
                  w-full glass rounded-2xl px-4 py-3.5 min-h-[52px] pl-12 pr-4
                  text-white placeholder:text-white/30
                  outline-none border border-transparent
                  transition-all duration-200
                  focus:border-accent-blue/50 focus:bg-white/[0.08]
                  hover:bg-white/[0.06]
                  ${errors.confirmPassword ? 'border-red-500/50 bg-red-500/5' : ''}
                  ${confirmPassword && confirmPassword === password ? 'border-green-500/50' : ''}
                `}
              />
              {confirmPassword && confirmPassword === password && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-400">
                  <Check size={20} />
                </div>
              )}
            </div>
            {errors.confirmPassword && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-red-400 ml-1"
              >
                {errors.confirmPassword}
              </motion.p>
            )}
          </div>

          {/* Terms */}
          <p className="text-white/40 text-xs text-center py-3">
            By creating an account, you agree to our{' '}
            <button type="button" className="text-accent-blue hover:underline">Terms of Service</button> and{' '}
            <button type="button" className="text-accent-blue hover:underline">Privacy Policy</button>
          </p>

          {/* Submit */}
          <Button
            type="submit"
            fullWidth
            size="lg"
            loading={isLoading}
            icon={!isLoading && <ArrowRight size={20} />}
          >
            {isLoading ? 'Creating account...' : 'Create Account'}
          </Button>
        </motion.form>

        {/* Login Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-auto py-6 text-center"
        >
          <p className="text-white/50">
            Already have an account?{' '}
            <button
              onClick={onLogin}
              className="text-accent-blue font-semibold hover:text-accent-blue/80 transition-colors"
            >
              Sign In
            </button>
          </p>
        </motion.div>
      </div>

      {/* Safe Area Bottom */}
      <div className="safe-area-bottom" />
    </motion.div>
  );
}
